import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useLandingT } from '../i18n/landingTranslations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChefHat, Lock, Eye, EyeOff, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useLandingT(language);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t('auth_reset_mismatch'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('auth_reset_min_length'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setDone(true);
        toast.success(t('auth_reset_success'));
      } else {
        const data = await res.json();
        toast.error(data.detail || t('auth_reset_error'));
      }
    } catch {
      toast.error(t('auth_error'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-[#888] mb-4">{t('auth_reset_invalid')}</p>
          <Link to="/auth" className="text-[#D4A853] hover:underline">{t('auth_back_login')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-4 mb-8">
          <Link to="/" className="flex items-center gap-2">
            <ChefHat className="w-8 h-8 text-[#D4A853]" />
            <span className="text-2xl font-black text-white tracking-tight">HIBI<span className="text-[#D4A853]">SAAS</span></span>
          </Link>
        </div>

        <div className="bg-[#1A1A1A] rounded-3xl border border-[#2A2A2A] p-8" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">{t('auth_reset_done_title')}</h2>
              <p className="text-[#888] text-sm mb-6">{t('auth_reset_done_desc')}</p>
              <Button onClick={() => navigate('/auth')}
                className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold rounded-xl"
                data-testid="go-to-login-btn">
                {t('auth_submit_login')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-white mb-1" data-testid="reset-title">{t('auth_reset_title')}</h1>
              <p className="text-sm text-[#888] mb-6">{t('auth_reset_desc')}</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth_reset_new_password')} required minLength={6}
                    className="pl-10 pr-10 h-12 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                    data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input type={showPassword ? 'text' : 'password'} value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth_reset_confirm_password')} required minLength={6}
                    className="pl-10 h-12 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                    data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-12 bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold rounded-xl text-sm"
                  data-testid="reset-submit-btn">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>{t('auth_reset_submit')} <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
